import { CandidateTrajectory } from "./CandidateTrajectory.js";
import { Orbit } from "./Orbit.js";
import { OrbitConnector } from "./OrbitConnector.js";
import { PointCandidateTrajectory } from "./PointCandidateTrajectory.js";
import { Tangent } from "./Tangent.js";
import { FullTrajectory } from "./FullTrajectory.js";
import { EndTrajectory } from "./EndTrajectory.js";
import { V } from "../misc/Vector.js";
import { Intersects } from "../utils/Intersects.js";
import Flatten from "@flatten-js/core";
export { SearchNode };
class SearchNode {
    constructor({ activeGraph, candidateTrajectory, mandatoryWaypointsState, previous }) {
        this.activeGraph = activeGraph;
        this.candidateTrajectory = candidateTrajectory;
        this.mandatoryWaypointsState = mandatoryWaypointsState;
        this.previous = previous;
    }
    static startWaypoints({ searchSpace, activeGraph, mandatoryWaypointsState, port, connection }) {
        if (searchSpace.activeGraph !== activeGraph) {
            throw new Error('Active graph does not match current graph');
        }
        const startWaypoints = searchSpace.findPortWaypoints({ port, connection });
        startWaypoints.sort((a, b) => a.distance - b.distance);
        const useStartWaypoints = startWaypoints.slice(0, 2);
        const nodes = useStartWaypoints.map(candidate => SearchNode.startWaypoint({ searchSpace, activeGraph, mandatoryWaypointsState, waypoint: candidate.waypoint, port, connection })).filter(n => n !== undefined);
        if (nodes.length === 0) {
            return undefined;
        }
        else {
            return nodes;
        }
    }
    static startWaypoint({ searchSpace, activeGraph, mandatoryWaypointsState, waypoint, port, connection }) {
        if (waypoint.trajectories.length !== 0) {
            return undefined;
        }
        const clonedMandatoryWaypointState = mandatoryWaypointsState.clone();
        if (!clonedMandatoryWaypointState.isAllowed(waypoint.id)) {
            return undefined;
        }
        else {
            clonedMandatoryWaypointState.moveNext(waypoint.id);
        }
        const clonedGraph = activeGraph.clone();
        if (!activeGraph.waypoints.has(waypoint.id)) {
            const clonedWaypoint = waypoint.clone();
            const radius = V.distance(waypoint.position, port.position);
            const trajectory = new PointCandidateTrajectory({
                point: port.position,
                direction: port.direction,
                waypoint: clonedWaypoint,
                center: clonedWaypoint.position,
                radius: radius,
                width: connection.width,
                spacing: connection.spacing,
                connection
            });
            clonedWaypoint.trajectories.push(trajectory);
            clonedGraph.activeGraph.waypoints.set(clonedWaypoint.id, clonedWaypoint);
            clonedGraph.activeGraph.updateWaypoint(clonedWaypoint, waypoint);
            return new SearchNode({
                activeGraph: clonedGraph.activeGraph,
                candidateTrajectory: trajectory,
                mandatoryWaypointsState: clonedMandatoryWaypointState,
                previous: undefined
            });
        }
        else {
            throw new Error('TODO');
        }
    }
    static endTrajectory({ searchSpace, current, port, connection }) {
        if (searchSpace.activeGraph !== current.activeGraph) {
            throw new Error('Unexpected case');
        }
        const clonedGraph = current.activeGraph.clone();
        const trajectory = clonedGraph.trajectoryMap.get(current.candidateTrajectory);
        if (!(trajectory instanceof CandidateTrajectory)) {
            throw new Error('Unexpected case');
        }
        const replacementTrajectory = new EndTrajectory({
            tangent: trajectory.tangent,
            point: port.position,
            direction: port.direction,
            rotation: trajectory.waypoint.rotationOfPoint({ point: port.position, direction: port.direction }),
            waypoint: trajectory.waypoint,
            center: trajectory.center,
            radius: trajectory.radius,
            width: connection.width,
            spacing: connection.spacing,
            connection
        });
        replacementTrajectory.tangent.targetTrajectory = replacementTrajectory;
        const replacementIndex = trajectory.waypoint.trajectories.indexOf(trajectory);
        if (replacementIndex === -1) {
            throw new Error('Unexpected case');
        }
        trajectory.waypoint.trajectories[replacementIndex] = replacementTrajectory;
        const replacementArc = replacementTrajectory.getArc();
        if (searchSpace.arcIntersectsWaypointTrajectories(replacementArc, replacementTrajectory.waypoint, replacementTrajectory)) {
            return;
        }
        if (searchSpace.arcIntersectsBarriers(replacementArc)) {
            return;
        }
        if (searchSpace.waypointOverlapsBarrier(replacementTrajectory.waypoint)) {
            return;
        }
        if (searchSpace.waypointOverlapsTangent(replacementTrajectory.waypoint, connection.width, connection.spacing, replacementTrajectory.waypoint.tangents.map(t => t.id))) {
            return;
        }
        if (searchSpace.waypointOverlapsWaypoint(replacementTrajectory.waypoint)) {
            return;
        }
        const arcCost = (replacementArc instanceof Flatten.Arc) ? replacementArc.length : 0;
        return {
            node: new SearchNode({
                activeGraph: clonedGraph.activeGraph,
                candidateTrajectory: replacementTrajectory,
                mandatoryWaypointsState: current.mandatoryWaypointsState.clone(),
                previous: current
            }),
            cost: arcCost
        };
    }
    static waypoint({ searchSpace, current, waypoint, connection, visitedNodes, callStack }) {
        if (searchSpace.activeGraph !== current.activeGraph) {
            throw new Error('Active graph does not match current graph');
        }
        const indices = searchSpace.findConnectableOrbitIndices({
            current,
            waypoint,
            connection
        });
        const nodes = indices.map(index => SearchNode.waypointWithIndex({
            searchSpace,
            current,
            waypoint,
            index,
            connection,
            visitedNodes,
            callStack
        }));
        const definedNodes = nodes.filter(node => node !== undefined);
        return definedNodes.flat();
    }
    static endPortWaypoint({ searchSpace, current, waypoint, port, connection, visitedNodes, callStack }) {
        const radius = Math.hypot(port.position[0] - waypoint.position[0], port.position[1] - waypoint.position[1]);
        return SearchNode.waypointWithIndex({ searchSpace, current, waypoint, index: 0, minRadius: radius, connection, visitedNodes, callStack });
    }
    static waypointWithIndex({ searchSpace, current, waypoint, index, minRadius = 0, connection, visitedNodes, callStack }) {
        const MAX_CALLSTACK = 100;
        if (searchSpace.activeGraph !== current.activeGraph) {
            throw new Error('Active graph does not match current graph');
        }
        const mandatoryWaypointsState = current.mandatoryWaypointsState.clone();
        if (!mandatoryWaypointsState.isAllowed(waypoint.id)) {
            return undefined;
        }
        else {
            mandatoryWaypointsState.moveNext(waypoint.id);
        }
        if (current.candidateTrajectory instanceof EndTrajectory) {
            throw new Error('Unexpected case');
        }
        const actualWaypoint = waypoint;
        if (index < actualWaypoint.trajectories.length) {
            let nextRadius;
            if (index === 0) {
                nextRadius = Orbit.zeroOrbit({
                    waypoint: actualWaypoint,
                    center: actualWaypoint.position,
                    spacing: actualWaypoint.minDistance
                }).nextRadius({
                    width: connection.width,
                    spacing: connection.spacing * actualWaypoint.innerSpacingFactor,
                    minBendradius: connection.minBendradius
                });
            }
            else {
                const innerOrbit = actualWaypoint.trajectories[index - 1];
                nextRadius = innerOrbit.nextRadius({
                    width: connection.width,
                    spacing: connection.spacing,
                    minBendradius: connection.minBendradius
                });
            }
            const higher = actualWaypoint.trajectories[index];
            if (nextRadius + 0.5 * connection.width + 0.5 * higher.width + Math.max(connection.spacing, higher.spacing) > higher.radius) {
                return undefined;
            }
        }
        if (index > actualWaypoint.trajectories.length) {
            throw new Error('Invalid index');
        }
        let computedRadius = 0;
        if (index === 0) {
            computedRadius = Orbit.zeroOrbit({
                waypoint: actualWaypoint,
                center: actualWaypoint.position,
                spacing: actualWaypoint.minDistance
            }).nextRadius({
                width: connection.width,
                spacing: connection.spacing * actualWaypoint.innerSpacingFactor,
                minBendradius: connection.minBendradius
            });
        }
        else {
            const innerOrbit = actualWaypoint.trajectories[index - 1];
            computedRadius = innerOrbit.nextRadius({
                width: connection.width,
                spacing: connection.spacing,
                minBendradius: connection.minBendradius
            });
        }
        const radius = computedRadius < minRadius ? minRadius : computedRadius;
        const targetOrbit = new Orbit({
            waypoint: actualWaypoint,
            center: actualWaypoint.position,
            radius,
            width: connection.width,
            spacing: connection.spacing
        });
        if (actualWaypoint.trajectories.length > index) {
            // TODO
            return undefined;
        }
        const startRotation = current.candidateTrajectory.rotation;
        const tangents = OrbitConnector.withFixedStartPoint({
            startOrbit: current.candidateTrajectory,
            startRotation: startRotation,
            targetOrbit,
        });
        if (tangents.length === 0) {
            return undefined;
        }
        const results = [];
        tangents.forEach((tangent, i) => {
            //TODO: Epsilon-safe tangent
            if (current.candidateTrajectory instanceof CandidateTrajectory && current.previous !== undefined) {
                if (Intersects.segmentSegment(tangent.startPoint, tangent.endPoint, current.candidateTrajectory.tangent.sourcePoint, current.candidateTrajectory.tangent.targetPoint, true, true, false)) {
                    if (callStack !== undefined && callStack >= MAX_CALLSTACK) {
                        return;
                    }
                    searchSpace.activeGraph = current.previous.activeGraph;
                    const nodes = SearchNode.waypoint({
                        searchSpace,
                        current: current.previous,
                        waypoint,
                        connection,
                        visitedNodes,
                        callStack: callStack ? callStack + 1 : 1
                    });
                    if (nodes !== undefined) {
                        results.push(...nodes);
                    }
                    searchSpace.activeGraph = current.activeGraph;
                    return;
                }
            }
            if (visitedNodes.visited({
                fromWaypointId: current.candidateTrajectory.waypoint.id,
                toWaypointId: actualWaypoint.id,
                fromRadius: current.candidateTrajectory.radius,
                toRadius: targetOrbit.radius,
                fromAngle: V.angle(V.subtract(tangent.startPoint, current.candidateTrajectory.center)),
                toAngle: V.angle(V.subtract(tangent.endPoint, targetOrbit.center))
            })) {
                return;
            }
            const influencingWaypoint = searchSpace.influencingWaypoint(tangent.startPoint, tangent.endPoint, connection.width, connection.spacing, [current.candidateTrajectory.waypoint.id, waypoint.id]);
            if (influencingWaypoint !== undefined) {
                if (callStack !== undefined && callStack >= MAX_CALLSTACK) {
                    return;
                }
                const nodes = SearchNode.waypoint({
                    searchSpace,
                    current,
                    waypoint: influencingWaypoint,
                    connection,
                    visitedNodes,
                    callStack: callStack ? callStack + 1 : 1
                });
                if (nodes !== undefined) {
                    results.push(...nodes);
                }
                return;
            }
            if (searchSpace.intersectsBarriers(tangent.startPoint, tangent.endPoint)) {
                return;
            }
            if (searchSpace.intersectsTangents(tangent.startPoint, tangent.endPoint)) {
                return;
            }
            if (searchSpace.intersectsWaypointTrajectories(actualWaypoint, tangent.startPoint, tangent.endPoint)) {
                return undefined;
            }
            const cloned = current.activeGraph.clone();
            const candidateTrajectory = cloned.trajectoryMap.get(current.candidateTrajectory);
            if (candidateTrajectory === undefined) {
                throw new Error('Unexpected case');
            }
            const clonedWaypoint = !cloned.activeGraph.waypoints.has(actualWaypoint.id) ? actualWaypoint.clone() : cloned.activeGraph.waypoints.get(actualWaypoint.id);
            if (clonedWaypoint.rotation !== undefined && Math.sign(clonedWaypoint.rotation) !== Math.sign(tangent.endRotation)) {
                return;
            }
            if (!cloned.activeGraph.waypoints.has(actualWaypoint.id)) {
                cloned.activeGraph.waypoints.set(clonedWaypoint.id, clonedWaypoint);
            }
            const graphTangent = new Tangent({
                sourcePoint: tangent.startPoint,
                targetPoint: tangent.endPoint,
                direction: tangent.direction,
                vector: tangent.direction,
                sourceRotation: tangent.startRotation,
                targetRotation: tangent.endRotation,
                width: connection.width
            });
            cloned.activeGraph.addTangent(graphTangent);
            const targetTrajectory = new CandidateTrajectory({
                tangent: graphTangent,
                point: tangent.endPoint,
                direction: tangent.direction,
                waypoint: clonedWaypoint,
                center: clonedWaypoint.position,
                radius: radius,
                width: connection.width,
                spacing: connection.spacing,
                connection
            });
            clonedWaypoint.trajectories.splice(index, 0, targetTrajectory);
            graphTangent.targetTrajectory = targetTrajectory;
            cloned.activeGraph.updateWaypoint(clonedWaypoint, actualWaypoint);
            let replacementTrajectory;
            if (candidateTrajectory instanceof CandidateTrajectory) {
                replacementTrajectory = new FullTrajectory({
                    tangents: [graphTangent, candidateTrajectory.tangent],
                    waypoint: candidateTrajectory.waypoint,
                    center: candidateTrajectory.center,
                    radius: candidateTrajectory.radius,
                    width: connection.width,
                    spacing: connection.spacing,
                    connection
                });
                if (candidateTrajectory.tangent.isSource(candidateTrajectory)) {
                    candidateTrajectory.tangent.sourceTrajectory = replacementTrajectory;
                }
                else if (candidateTrajectory.tangent.isTarget(candidateTrajectory)) {
                    candidateTrajectory.tangent.targetTrajectory = replacementTrajectory;
                }
                else {
                    throw new Error('Unexpected case');
                }
                graphTangent.sourceTrajectory = replacementTrajectory;
            }
            else if (candidateTrajectory instanceof PointCandidateTrajectory) {
                replacementTrajectory = new EndTrajectory({
                    tangent: graphTangent,
                    point: candidateTrajectory.point,
                    direction: candidateTrajectory.direction,
                    rotation: candidateTrajectory.rotation,
                    waypoint: candidateTrajectory.waypoint,
                    center: candidateTrajectory.center,
                    radius: candidateTrajectory.radius,
                    width: connection.width,
                    spacing: connection.spacing,
                    connection
                });
                graphTangent.sourceTrajectory = replacementTrajectory;
            }
            else {
                throw new Error('Unexpected case');
            }
            const replacementIndex = replacementTrajectory.waypoint.trajectories.indexOf(candidateTrajectory);
            if (replacementIndex === -1) {
                throw new Error('Unexpected case');
            }
            replacementTrajectory.waypoint.trajectories[replacementIndex] = replacementTrajectory;
            const replacementArc = replacementTrajectory.getArc();
            if (searchSpace.arcIntersectsWaypointTrajectories(replacementArc, replacementTrajectory.waypoint, replacementTrajectory)) {
                return;
            }
            if (searchSpace.arcIntersectsBarriers(replacementArc)) {
                return;
            }
            if (searchSpace.waypointOverlapsBarrier(replacementTrajectory.waypoint)) {
                return;
            }
            if (searchSpace.waypointOverlapsTangent(replacementTrajectory.waypoint, connection.width, connection.spacing, replacementTrajectory.waypoint.tangents.map(t => t.id))) {
                return;
            }
            if (searchSpace.waypointOverlapsWaypoint(replacementTrajectory.waypoint)) {
                return;
            }
            const arcCost = (replacementArc instanceof Flatten.Arc) ? replacementArc.length : 0;
            visitedNodes.visit({
                fromWaypointId: current.candidateTrajectory.waypoint.id,
                toWaypointId: actualWaypoint.id,
                fromRadius: current.candidateTrajectory.radius,
                toRadius: targetOrbit.radius,
                fromAngle: V.angle(V.subtract(tangent.startPoint, current.candidateTrajectory.center)),
                toAngle: V.angle(V.subtract(tangent.endPoint, targetOrbit.center))
            });
            const node = {
                node: new SearchNode({
                    activeGraph: cloned.activeGraph,
                    candidateTrajectory: targetTrajectory,
                    mandatoryWaypointsState,
                    previous: current
                }),
                cost: V.norm(graphTangent.vector) + arcCost
            };
            results.push(node);
        });
        return results;
    }
}
//# sourceMappingURL=SearchNode.js.map